// localFolder.ts — the LOCAL destination: a copy of the customer's data in a file OUTSIDE the app,
// wherever THEY pick (Mac: a folder; iPhone: "On My iPhone" in Files). It survives app deletion, so a
// local-first app is never a data dead-end. ON-DEMAND by design (Bogdan, "not rocket science"): the
// customer asks to save a copy, or to import one back — no background auto-sync. Same faithful backup
// envelope (exportBackup) as the cloud path. Standard: champion-lifecycle/CUSTOMER-CLOUD-STORAGE.md.

import { save as saveFileDialog, open as openDialog } from "@tauri-apps/plugin-dialog";
import { writeTextFile, readTextFile } from "@tauri-apps/plugin-fs";
import { exportBackup, backupFilename } from "./backup";

/** Save a one-off copy of the whole dataset to a location the user picks (the OS save / document picker
 *  IS the permission prompt — it scopes the write to exactly that spot). Returns true if saved, false if
 *  cancelled. Works on iPhone (Save to Files) and Mac. */
export async function saveCopyNow(appSlug: string): Promise<boolean> {
  const json = await exportBackup();
  const p = await saveFileDialog({ defaultPath: backupFilename(appSlug) });
  if (!p) return false;
  await writeTextFile(p, json);
  return true;
}

/** Open a backup file the user picks (Files / iCloud / a folder — wherever they bring it from) and
 *  return its text, so data can come back IN: after a delete+reinstall, from another device's export, or
 *  any compatible backup they have. The caller hands the string to importBackup (which validates the
 *  envelope). Returns null if they cancel. */
export async function importFromFile(): Promise<string | null> {
  const picked = await openDialog({ directory: false, multiple: false, filters: [{ name: "Backup", extensions: ["json"] }] });
  if (typeof picked !== "string" || !picked) return null;
  return readTextFile(picked);
}
